/**
 * i18n strings for AussieCV — EN, FR, DE
 */
export const translations = {
  en: {
    nav: { cta: "Optimize my CV" },
    hero: {
      overline: "Made for backpackers on 417 / 462",
      h1_a: "Don't let an algorithm",
      h1_b: "kill your dream job.",
      sub: "In Australia, 75% of CVs are rejected by AI before a human even sees them. AussieCV rewrites yours in the exact format Seek and the ATS expect — in under 60 seconds.",
      cta_primary: "Optimize my CV free",
      cta_secondary: "How it works",
      trust: "No account · No email · Your data stays yours",
    },
    stats: {
      rejected: { n: "75%", t: "of CVs filtered out by ATS before a human read them" },
      seek: { n: "92%", t: "of Australian recruiters source candidates through Seek" },
      time: { n: "60s", t: "to get your Australian-ready CV and cover letter" },
    },
    how: {
      title: "How it works",
      sub: "Four steps. Zero friction.",
      s1: { t: "Upload your CV", d: "Drop your current CV (PDF or Word). We extract the text in seconds." },
      s2: { t: "Paste the Seek job", d: "Paste the URL of the ad you want to nail. We fetch it automatically." },
      s3: { t: "Tell us the essentials", d: "Visa, location, sector. 30 seconds, no more." },
      s4: { t: "Get your optimized CV", d: "ATS score, rewritten CV, cover letter, download PDF/Word. Done." },
    },
    why: {
      title: "Why Australian recruiters filter so hard",
      p1: "Every big employer in Australia — mines, hospitality chains, healthcare, logistics — uses ATS software. The robot reads your CV before any human does.",
      p2: "If your keywords don't match the job ad, if your dates look wrong, if your visa isn't clear, or if your CV has columns, photos or a fancy layout — you're out. Silently.",
      p3: "AussieCV knows every rule of the Australian format and every keyword your sector expects. It rewrites your CV so the robot says YES and a human actually reads you.",
    },
    wizard: {
      step: "Step",
      of: "of",
      back: "Back",
      next: "Next",
      optimize: "Optimize my CV",
      s1: {
        title: "Upload your current CV",
        sub: "PDF, Word or text. We'll extract everything automatically.",
        drop: "Drop your CV here or click to browse",
        accepted: "PDF · DOCX · TXT — 10 MB max",
        or: "or paste your CV text",
        placeholder: "Paste your CV text here...",
        uploading: "Reading your CV...",
        parsed: "CV loaded",
      },
      s2: {
        title: "The job you're applying for",
        sub: "3 ways to import: URL, paste text, or upload a screenshot.",
        url_label: "Seek URL",
        url_placeholder: "https://www.seek.com.au/job/...",
        fetch: "Fetch job",
        fetching: "Fetching job...",
        loaded: "Job loaded",
        or: "or paste the job description",
        text_placeholder: "Paste the full job ad here...",
        seek_blocked_title: "Seek protection detected",
        seek_blocked_body: "Seek blocks automated requests. No worries — just copy-paste the job description text into the field below. Works just as well.",
      },
      s3: {
        title: "A few essentials",
        sub: "Takes less than 30 seconds. We only ask what the ATS actually checks.",
        visa: "Visa status",
        location: "Current city in Australia",
        location_ph: "e.g. Perth, Karratha, Sydney, Darwin...",
        sector: "Target sector",
        notes: "Anything specific to mention? (optional)",
        notes_ph: "Tickets you hold, availability, specific experience...",
        language: "Language for issues & checklist",
      },
      s4: {
        title: "Optimizing your CV",
        line1: "Reading your original CV...",
        line2: "Extracting keywords from the job ad...",
        line3: "Rewriting in Australian ATS format...",
        line4: "Generating your cover letter...",
        line5: "Computing your match score...",
        wait: "This usually takes 20 to 40 seconds.",
      },
    },
    visas: {
      "417": "417 Working Holiday Visa",
      "462": "462 Work & Holiday Visa",
      "482": "482 TSS Visa",
      "494": "494 Skilled Employer Sponsored",
      "500": "500 Student Visa",
      "PR": "Permanent Resident",
      "Citizen": "Australian Citizen",
      "Other": "Other / Bridging",
    },
    sectors: {
      mining: "Mining / FIFO",
      hospitality: "Hospitality / Café / Bar",
      construction: "Construction / Trades",
      farm: "Farm / Agriculture (88 days)",
      healthcare: "Healthcare / Aged care",
      tech: "Tech / Office",
      retail: "Retail / Customer service",
      logistics: "Logistics / Driving",
      other: "Other",
    },
    results: {
      title: "Your optimized CV is ready",
      sub: "Download it, polish if needed, and apply with confidence.",
      before: "Before",
      after: "After",
      score_label: "ATS score",
      issues: "Issues we fixed",
      improvements: "What we improved",
      missing: "Critical things you may be missing",
      checklist: "Your application checklist",
      cv_tab: "Optimized CV",
      cover_tab: "Cover Letter",
      download_pdf: "Download CV (PDF)",
      download_docx: "Download CV (Word)",
      download_cover: "Download Cover Letter (PDF)",
      download_action_plan: "Action Plan (PDF)",
      redo: "Start over",
      copy: "Copy to clipboard",
      copied: "Copied!",
    },
    faq: {
      title: "Common questions",
      q1: "Is my data safe?",
      a1: "We don't require an account or email. Your CV is processed by our secure AI engine to be rewritten in the Australian ATS format, then stored anonymously so we can improve the service. We never sell or share it.",
      q2: "Is this really free?",
      a2: "Yes, the MVP is 100% free. In the future we may introduce a premium plan (unlimited CVs, cover letter variants, LinkedIn optimizer), but your current usage stays free.",
      q3: "Will it work for non-mining jobs?",
      a3: "Absolutely. We cover Hospitality, Construction, Farm, Healthcare, Trades, Tech, Retail, Logistics and more. The AI adapts the keywords to your target sector.",
      q4: "Do I need an Australian address?",
      a4: "No. If you are abroad, we will frame your profile as 'willing to relocate'. If you are already in Australia, being local is a huge plus we will highlight.",
      q5: "What if I don't have all the required tickets?",
      a5: "We'll tell you exactly which ones are missing and where to get them (White Card, RSA, Standard 11, PTD, etc.). We never invent certifications you don't have.",
    },
    errors: {
      upload: "Couldn't read this file. Try a PDF or DOCX.",
      seek: "Couldn't fetch this Seek URL. Try pasting the job text instead.",
      optimize: "Something went wrong. Please try again.",
      missing_cv: "Please upload or paste your CV first.",
      missing_job: "Please provide the job ad.",
      missing_profile: "Please fill in visa, location and sector.",
    },
    footer: {
      tagline: "Built by backpackers, for backpackers.",
      legal: "Not affiliated with Seek or any employer.",
    },
  },

  fr: {
    nav: { cta: "Optimiser mon CV" },
    hero: {
      overline: "Fait pour les backpackers en 417 / 462",
      h1_a: "Ne laisse pas un algo",
      h1_b: "flinguer ton job de rêve.",
      sub: "En Australie, 75 % des CV sont rejetés par une IA avant même qu'un humain les voie. AussieCV réécrit le tien au format exact que Seek et les ATS attendent — en moins de 60 secondes.",
      cta_primary: "Optimiser mon CV gratuitement",
      cta_secondary: "Comment ça marche",
      trust: "Sans compte · Sans email · Tes données t'appartiennent",
    },
    stats: {
      rejected: { n: "75 %", t: "des CV filtrés par l'ATS avant lecture humaine" },
      seek: { n: "92 %", t: "des recruteurs australiens sourcent via Seek" },
      time: { n: "60 s", t: "pour avoir ton CV + lettre prêts à postuler" },
    },
    how: {
      title: "Comment ça marche",
      sub: "Quatre étapes. Zéro friction.",
      s1: { t: "Upload ton CV", d: "Dépose ton CV actuel (PDF ou Word). On extrait le texte en quelques secondes." },
      s2: { t: "Colle l'annonce Seek", d: "Colle l'URL de l'annonce que tu vises. On la récupère automatiquement." },
      s3: { t: "Dis-nous l'essentiel", d: "Visa, ville, secteur. 30 secondes, pas plus." },
      s4: { t: "Récupère ton CV optimisé", d: "Score ATS, CV réécrit, lettre de motivation, téléchargement PDF/Word. Fini." },
    },
    why: {
      title: "Pourquoi les recruteurs australiens filtrent si fort",
      p1: "Tous les gros employeurs en Australie — mines, chaînes hôtelières, santé, logistique — utilisent un logiciel ATS. Le robot lit ton CV avant tout humain.",
      p2: "Si tes mots-clés ne matchent pas l'annonce, si tes dates sont bancales, si ton visa n'est pas clair, ou si ton CV a des colonnes, une photo ou un design chelou — t'es out. Sans rien dire.",
      p3: "AussieCV connaît toutes les règles du format australien et tous les mots-clés que ton secteur exige. On réécrit ton CV pour que le robot dise OUI et qu'un humain te lise vraiment.",
    },
    wizard: {
      step: "Étape",
      of: "sur",
      back: "Retour",
      next: "Suivant",
      optimize: "Optimiser mon CV",
      s1: {
        title: "Upload ton CV actuel",
        sub: "PDF, Word ou texte. On extrait tout automatiquement.",
        drop: "Dépose ton CV ici ou clique pour choisir",
        accepted: "PDF · DOCX · TXT — 10 Mo max",
        or: "ou colle le texte de ton CV",
        placeholder: "Colle le texte de ton CV ici...",
        uploading: "Lecture de ton CV...",
        parsed: "CV chargé",
      },
      s2: {
        title: "L'annonce que tu vises",
        sub: "3 façons d'importer : URL, coller le texte, ou upload un screenshot.",
        url_label: "URL Seek",
        url_placeholder: "https://www.seek.com.au/job/...",
        fetch: "Récupérer l'annonce",
        fetching: "Récupération...",
        loaded: "Annonce chargée",
        or: "ou colle la description",
        text_placeholder: "Colle l'annonce complète ici...",
        seek_blocked_title: "Seek bloque la récupération auto",
        seek_blocked_body: "Pas de souci ! Copie-colle simplement le texte de l'annonce dans le champ ci-dessous, ça marchera aussi bien.",
      },
      s3: {
        title: "Quelques essentiels",
        sub: "Moins de 30 secondes. On demande uniquement ce que l'ATS vérifie vraiment.",
        visa: "Statut de visa",
        location: "Ville actuelle en Australie",
        location_ph: "ex. Perth, Karratha, Sydney, Darwin...",
        sector: "Secteur visé",
        notes: "Quelque chose à ajouter ? (optionnel)",
        notes_ph: "Tickets que tu as, disponibilité, expérience spécifique...",
        language: "Langue du rapport (analyses & checklist)",
      },
      s4: {
        title: "Optimisation en cours",
        line1: "Lecture de ton CV original...",
        line2: "Extraction des mots-clés de l'annonce...",
        line3: "Réécriture au format ATS australien...",
        line4: "Génération de ta lettre de motivation...",
        line5: "Calcul de ton score de match...",
        wait: "Ça prend généralement 20 à 40 secondes.",
      },
    },
    visas: {
      "417": "417 Working Holiday Visa",
      "462": "462 Work & Holiday Visa",
      "482": "482 Visa TSS",
      "494": "494 Skilled Employer Sponsored",
      "500": "500 Visa Étudiant",
      "PR": "Résident Permanent",
      "Citizen": "Citoyen australien",
      "Other": "Autre / Bridging",
    },
    sectors: {
      mining: "Mines / FIFO",
      hospitality: "Hôtellerie / Café / Bar",
      construction: "Construction / BTP",
      farm: "Ferme / Agriculture (88 jours)",
      healthcare: "Santé / Aged care",
      tech: "Tech / Bureau",
      retail: "Retail / Service client",
      logistics: "Logistique / Conduite",
      other: "Autre",
    },
    results: {
      title: "Ton CV optimisé est prêt",
      sub: "Télécharge-le, ajuste si besoin, et postule en confiance.",
      before: "Avant",
      after: "Après",
      score_label: "Score ATS",
      issues: "Problèmes corrigés",
      improvements: "Ce qu'on a amélioré",
      missing: "Ce qui manque de critique",
      checklist: "Ta checklist avant d'envoyer",
      cv_tab: "CV optimisé",
      cover_tab: "Lettre de motivation",
      download_pdf: "Télécharger CV (PDF)",
      download_docx: "Télécharger CV (Word)",
      download_cover: "Télécharger lettre (PDF)",
      download_action_plan: "Plan d'action (PDF)",
      redo: "Recommencer",
      copy: "Copier",
      copied: "Copié !",
    },
    faq: {
      title: "Questions fréquentes",
      q1: "Mes données sont-elles en sécurité ?",
      a1: "On ne demande ni compte ni email. Ton CV est traité par notre moteur d'IA sécurisé pour être réécrit au format ATS australien, puis stocké anonymement pour améliorer le service. On ne vend ni ne partage rien.",
      q2: "C'est vraiment gratuit ?",
      a2: "Oui, le MVP est 100 % gratuit. Plus tard on introduira peut-être une offre premium (CV illimités, variantes de lettres, LinkedIn optimizer), mais ton utilisation actuelle reste gratuite.",
      q3: "Ça marche pour les jobs hors mines ?",
      a3: "Carrément. On couvre Hôtellerie, Construction, Ferme, Santé, Trades, Tech, Retail, Logistique et plus. L'IA adapte les mots-clés à ton secteur.",
      q4: "Je dois avoir une adresse australienne ?",
      a4: "Non. Si tu es à l'étranger, on présente ton profil comme « willing to relocate ». Si tu es déjà en Australie, être local c'est un énorme plus qu'on met en avant.",
      q5: "Et si je n'ai pas tous les tickets requis ?",
      a5: "On te dit exactement lesquels manquent et où les obtenir (White Card, RSA, Standard 11, PTD, etc.). On n'invente jamais de certifications que tu n'as pas.",
    },
    errors: {
      upload: "Lecture du fichier impossible. Essaye un PDF ou DOCX.",
      seek: "Impossible de récupérer cette URL Seek. Colle le texte de l'annonce à la place.",
      optimize: "Une erreur est survenue. Réessaye.",
      missing_cv: "Upload ou colle ton CV d'abord.",
      missing_job: "Ajoute l'annonce d'abord.",
      missing_profile: "Remplis visa, ville et secteur.",
    },
    footer: {
      tagline: "Construit par des backpackers, pour des backpackers.",
      legal: "Pas affilié à Seek ni à aucun employeur.",
    },
  },

  de: {
    nav: { cta: "Meinen CV optimieren" },
    hero: {
      overline: "Für Backpacker auf 417 / 462",
      h1_a: "Lass keinen Algorithmus",
      h1_b: "deinen Traumjob töten.",
      sub: "In Australien werden 75 % der CVs von einer KI aussortiert, bevor ein Mensch sie sieht. AussieCV schreibt deinen CV genau so um, wie Seek und die ATS ihn erwarten — in unter 60 Sekunden.",
      cta_primary: "CV kostenlos optimieren",
      cta_secondary: "So funktioniert's",
      trust: "Ohne Konto · Ohne E-Mail · Deine Daten bleiben deine",
    },
    stats: {
      rejected: { n: "75 %", t: "der CVs werden vom ATS aussortiert, bevor ein Mensch sie liest" },
      seek: { n: "92 %", t: "der australischen Recruiter nutzen Seek" },
      time: { n: "60 s", t: "bis dein australischer CV und das Anschreiben fertig sind" },
    },
    how: {
      title: "So funktioniert's",
      sub: "Vier Schritte. Null Reibung.",
      s1: { t: "CV hochladen", d: "Lade deinen aktuellen CV hoch (PDF oder Word). Wir extrahieren den Text in Sekunden." },
      s2: { t: "Seek-Anzeige einfügen", d: "Füge die URL der Stellenanzeige ein. Wir holen sie automatisch." },
      s3: { t: "Das Wichtigste sagen", d: "Visum, Standort, Branche. 30 Sekunden, mehr nicht." },
      s4: { t: "Optimierten CV erhalten", d: "ATS-Score, neuer CV, Anschreiben, Download als PDF/Word. Fertig." },
    },
    why: {
      title: "Warum australische Recruiter so hart filtern",
      p1: "Jeder große Arbeitgeber in Australien — Minen, Hotellerie, Gesundheit, Logistik — nutzt ATS-Software. Der Roboter liest deinen CV, bevor ein Mensch es tut.",
      p2: "Wenn deine Keywords nicht passen, Daten falsch aussehen, dein Visum nicht klar ist oder dein CV Spalten/Foto hat — bist du raus. Stillschweigend.",
      p3: "AussieCV kennt alle Regeln des australischen Formats und alle Keywords deiner Branche. Wir schreiben deinen CV so um, dass der Roboter JA sagt und ein Mensch dich wirklich liest.",
    },
    wizard: {
      step: "Schritt",
      of: "von",
      back: "Zurück",
      next: "Weiter",
      optimize: "CV optimieren",
      s1: {
        title: "Lade deinen aktuellen CV hoch",
        sub: "PDF, Word oder Text. Wir extrahieren alles automatisch.",
        drop: "CV hier ablegen oder klicken",
        accepted: "PDF · DOCX · TXT — max. 10 MB",
        or: "oder CV-Text einfügen",
        placeholder: "CV-Text hier einfügen...",
        uploading: "CV wird gelesen...",
        parsed: "CV geladen",
      },
      s2: {
        title: "Die Stelle, für die du dich bewirbst",
        sub: "3 Import-Optionen: URL, Text einfügen oder Screenshot hochladen.",
        url_label: "Seek-URL",
        url_placeholder: "https://www.seek.com.au/job/...",
        fetch: "Job holen",
        fetching: "Wird geholt...",
        loaded: "Job geladen",
        or: "oder Anzeige einfügen",
        text_placeholder: "Komplette Anzeige hier einfügen...",
        seek_blocked_title: "Seek blockiert den automatischen Abruf",
        seek_blocked_body: "Kein Problem! Kopiere und füge einfach den Text der Stellenanzeige ins Feld unten ein – funktioniert genauso gut.",
      },
      s3: {
        title: "Ein paar wichtige Infos",
        sub: "Weniger als 30 Sekunden. Wir fragen nur, was das ATS wirklich prüft.",
        visa: "Visum-Status",
        location: "Aktuelle Stadt in Australien",
        location_ph: "z.B. Perth, Karratha, Sydney, Darwin...",
        sector: "Ziel-Branche",
        notes: "Etwas Besonderes? (optional)",
        notes_ph: "Zertifikate, Verfügbarkeit, spezifische Erfahrung...",
        language: "Sprache für Bericht & Checkliste",
      },
      s4: {
        title: "Dein CV wird optimiert",
        line1: "Lese deinen Original-CV...",
        line2: "Extrahiere Keywords aus der Anzeige...",
        line3: "Schreibe im australischen ATS-Format...",
        line4: "Generiere dein Anschreiben...",
        line5: "Berechne deinen Match-Score...",
        wait: "Dauert normalerweise 20 bis 40 Sekunden.",
      },
    },
    visas: {
      "417": "417 Working Holiday Visum",
      "462": "462 Work & Holiday Visum",
      "482": "482 TSS Visum",
      "494": "494 Skilled Employer Sponsored",
      "500": "500 Student Visum",
      "PR": "Dauerhafter Aufenthalt",
      "Citizen": "Australischer Staatsbürger",
      "Other": "Andere / Bridging",
    },
    sectors: {
      mining: "Bergbau / FIFO",
      hospitality: "Hotellerie / Café / Bar",
      construction: "Bau / Handwerk",
      farm: "Farm / Landwirtschaft (88 Tage)",
      healthcare: "Gesundheit / Aged care",
      tech: "Tech / Büro",
      retail: "Einzelhandel / Kundenservice",
      logistics: "Logistik / Fahren",
      other: "Andere",
    },
    results: {
      title: "Dein optimierter CV ist fertig",
      sub: "Herunterladen, bei Bedarf anpassen und selbstbewusst bewerben.",
      before: "Vorher",
      after: "Nachher",
      score_label: "ATS-Score",
      issues: "Behobene Probleme",
      improvements: "Was wir verbessert haben",
      missing: "Kritische fehlende Punkte",
      checklist: "Deine Bewerbungs-Checkliste",
      cv_tab: "Optimierter CV",
      cover_tab: "Anschreiben",
      download_pdf: "CV herunterladen (PDF)",
      download_docx: "CV herunterladen (Word)",
      download_cover: "Anschreiben (PDF)",
      download_action_plan: "Aktionsplan (PDF)",
      redo: "Neu starten",
      copy: "Kopieren",
      copied: "Kopiert!",
    },
    faq: {
      title: "Häufige Fragen",
      q1: "Sind meine Daten sicher?",
      a1: "Kein Konto, keine E-Mail nötig. Dein Lebenslauf wird von unserer sicheren KI-Engine im australischen ATS-Format umgeschrieben und anschließend anonym gespeichert, um den Service zu verbessern. Wir verkaufen oder teilen nichts.",
      q2: "Ist das wirklich kostenlos?",
      a2: "Ja, das MVP ist zu 100 % kostenlos. Später gibt es vielleicht Premium (unbegrenzte CVs, Anschreiben-Varianten, LinkedIn-Optimierer), aber deine aktuelle Nutzung bleibt gratis.",
      q3: "Funktioniert es auch außerhalb des Bergbaus?",
      a3: "Absolut. Hotellerie, Bau, Farm, Gesundheit, Handwerk, Tech, Einzelhandel, Logistik und mehr. Die KI passt die Keywords an deine Branche an.",
      q4: "Brauche ich eine australische Adresse?",
      a4: "Nein. Im Ausland stellen wir dein Profil als 'willing to relocate' dar. In Australien ist lokal zu sein ein riesiger Pluspunkt, den wir betonen.",
      q5: "Was, wenn mir Zertifikate fehlen?",
      a5: "Wir sagen dir genau, welche fehlen und wo du sie bekommst (White Card, RSA, Standard 11, PTD, etc.). Wir erfinden nie Zertifikate.",
    },
    errors: {
      upload: "Datei konnte nicht gelesen werden. Versuche PDF oder DOCX.",
      seek: "Seek-URL konnte nicht geholt werden. Füge den Text ein.",
      optimize: "Ein Fehler ist aufgetreten. Bitte nochmal versuchen.",
      missing_cv: "Bitte zuerst den CV hochladen oder einfügen.",
      missing_job: "Bitte Anzeige angeben.",
      missing_profile: "Bitte Visum, Stadt und Branche ausfüllen.",
    },
    footer: {
      tagline: "Gebaut von Backpackern, für Backpacker.",
      legal: "Nicht mit Seek oder einem Arbeitgeber verbunden.",
    },
  },

  it: {
    nav: { cta: "Ottimizza il mio CV" },
    hero: {
      overline: "Pensato per i backpacker in 417 / 462",
      h1_a: "Non lasciare che un algoritmo",
      h1_b: "uccida il tuo lavoro da sogno.",
      sub: "In Australia, il 75% dei CV viene scartato da un'IA prima ancora che un umano li veda. AussieCV riscrive il tuo nel formato esatto che Seek e gli ATS si aspettano — in meno di 60 secondi.",
      cta_primary: "Ottimizza il mio CV gratis",
      cta_secondary: "Come funziona",
      trust: "Senza account · Senza email · I tuoi dati restano tuoi",
    },
    stats: {
      rejected: { n: "75%", t: "dei CV filtrati dall'ATS prima della lettura umana" },
      seek: { n: "92%", t: "dei recruiter australiani usano Seek" },
      time: { n: "60s", t: "per avere il tuo CV e cover letter pronti" },
    },
    how: {
      title: "Come funziona",
      sub: "Quattro passi. Zero attrito.",
      s1: { t: "Carica il tuo CV", d: "Trascina il tuo CV attuale (PDF o Word). Estraiamo il testo in pochi secondi." },
      s2: { t: "Incolla l'annuncio Seek", d: "Incolla l'URL dell'annuncio. Lo recuperiamo automaticamente." },
      s3: { t: "Dicci l'essenziale", d: "Visto, città, settore. 30 secondi, non di più." },
      s4: { t: "Ricevi il tuo CV ottimizzato", d: "Punteggio ATS, CV riscritto, cover letter, download PDF/Word. Fatto." },
    },
    why: {
      title: "Perché i recruiter australiani filtrano così duramente",
      p1: "Tutti i grandi datori di lavoro in Australia — miniere, hotel, sanità, logistica — usano software ATS. Il robot legge il tuo CV prima di qualsiasi umano.",
      p2: "Se le tue parole chiave non corrispondono all'annuncio, se le date sono strane, se il tuo visto non è chiaro, o se il CV ha colonne/foto/design strano — sei fuori. In silenzio.",
      p3: "AussieCV conosce tutte le regole del formato australiano e tutte le parole chiave che il tuo settore richiede. Riscriviamo il tuo CV affinché il robot dica SÌ e un umano ti legga davvero.",
    },
    wizard: {
      step: "Passo",
      of: "di",
      back: "Indietro",
      next: "Avanti",
      optimize: "Ottimizza il mio CV",
      s1: {
        title: "Carica il tuo CV attuale",
        sub: "PDF, Word o testo. Estraiamo tutto automaticamente.",
        drop: "Trascina il tuo CV qui o clicca per sfogliare",
        accepted: "PDF · DOCX · TXT — max 10 MB",
        or: "o incolla il testo del CV",
        placeholder: "Incolla qui il testo del tuo CV...",
        uploading: "Lettura del tuo CV...",
        parsed: "CV caricato",
      },
      s2: {
        title: "L'annuncio per cui ti candidi",
        sub: "3 modi per importare: URL, incolla testo o carica uno screenshot.",
        url_label: "URL Seek",
        url_placeholder: "https://www.seek.com.au/job/...",
        fetch: "Recupera annuncio",
        fetching: "Recupero...",
        loaded: "Annuncio caricato",
        or: "o incolla la descrizione",
        text_placeholder: "Incolla qui l'annuncio completo...",
        seek_blocked_title: "Seek blocca il recupero automatico",
        seek_blocked_body: "Nessun problema! Copia e incolla il testo dell'annuncio nel campo qui sotto, funziona altrettanto bene.",
      },
      s3: {
        title: "Qualche dato essenziale",
        sub: "Meno di 30 secondi. Chiediamo solo ciò che l'ATS verifica davvero.",
        visa: "Stato del visto",
        location: "Città attuale in Australia",
        location_ph: "es. Perth, Karratha, Sydney, Darwin...",
        sector: "Settore target",
        notes: "Qualcosa di specifico da menzionare? (opzionale)",
        notes_ph: "Certificati, disponibilità, esperienza specifica...",
        language: "Lingua del report",
      },
      s4: {
        title: "Ottimizzazione del tuo CV",
        line1: "Lettura del tuo CV originale...",
        line2: "Estrazione delle parole chiave dall'annuncio...",
        line3: "Riscrittura nel formato ATS australiano...",
        line4: "Generazione della cover letter...",
        line5: "Calcolo del punteggio di corrispondenza...",
        wait: "Di solito ci vogliono 20-40 secondi.",
      },
    },
    visas: {
      "417": "417 Working Holiday Visa",
      "462": "462 Work & Holiday Visa",
      "482": "482 Visto TSS",
      "494": "494 Skilled Employer Sponsored",
      "500": "500 Visto Studente",
      "PR": "Residente Permanente",
      "Citizen": "Cittadino Australiano",
      "Other": "Altro / Bridging",
    },
    sectors: {
      mining: "Miniere / FIFO",
      hospitality: "Hotellerie / Caffè / Bar",
      construction: "Edilizia / Mestieri",
      farm: "Fattoria / Agricoltura (88 giorni)",
      healthcare: "Sanità / Aged care",
      tech: "Tech / Ufficio",
      retail: "Retail / Servizio clienti",
      logistics: "Logistica / Guida",
      other: "Altro",
    },
    results: {
      title: "Il tuo CV ottimizzato è pronto",
      sub: "Scaricalo, perfezionalo se serve, e candidati con sicurezza.",
      before: "Prima",
      after: "Dopo",
      score_label: "Punteggio ATS",
      issues: "Problemi che abbiamo corretto",
      improvements: "Cosa abbiamo migliorato",
      missing: "Punti critici che ti potrebbero mancare",
      checklist: "La tua checklist di candidatura",
      cv_tab: "CV ottimizzato",
      cover_tab: "Cover Letter",
      download_pdf: "Scarica CV (PDF)",
      download_docx: "Scarica CV (Word)",
      download_cover: "Scarica Cover Letter (PDF)",
      download_action_plan: "Piano d'azione (PDF)",
      redo: "Ricomincia",
      copy: "Copia",
      copied: "Copiato!",
    },
    faq: {
      title: "Domande frequenti",
      q1: "I miei dati sono al sicuro?",
      a1: "Non richiediamo account né email. Il tuo CV viene elaborato dal nostro motore di IA sicuro e riscritto nel formato ATS australiano, poi archiviato in modo anonimo per migliorare il servizio. Non vendiamo né condividiamo nulla.",
      q2: "È davvero gratis?",
      a2: "Sì, l'MVP è 100% gratuito. In futuro potremmo introdurre un piano premium (CV illimitati, varianti di cover letter, LinkedIn optimizer), ma il tuo uso attuale rimane gratis.",
      q3: "Funziona anche per lavori fuori dalle miniere?",
      a3: "Assolutamente. Copriamo Hotellerie, Edilizia, Fattoria, Sanità, Mestieri, Tech, Retail, Logistica e altro. L'IA adatta le parole chiave al tuo settore.",
      q4: "Devo avere un indirizzo australiano?",
      a4: "No. Se sei all'estero, presentiamo il tuo profilo come 'willing to relocate'. Se sei già in Australia, essere locale è un enorme vantaggio che mettiamo in risalto.",
      q5: "E se non ho tutti i certificati richiesti?",
      a5: "Ti diciamo esattamente quali mancano e dove ottenerli (White Card, RSA, Standard 11, PTD, ecc.). Non inventiamo mai certificati che non hai.",
    },
    errors: {
      upload: "Impossibile leggere il file. Prova un PDF o DOCX.",
      seek: "Impossibile recuperare l'URL Seek. Incolla il testo dell'annuncio.",
      optimize: "Si è verificato un errore. Riprova.",
      missing_cv: "Carica o incolla prima il tuo CV.",
      missing_job: "Aggiungi prima l'annuncio.",
      missing_profile: "Compila visto, città e settore.",
    },
    footer: {
      tagline: "Costruito da backpacker, per backpacker.",
      legal: "Non affiliato a Seek né a nessun datore di lavoro.",
    },
  },

  es: {
    nav: { cta: "Optimizar mi CV" },
    hero: {
      overline: "Hecho para backpackers en 417 / 462",
      h1_a: "No dejes que un algoritmo",
      h1_b: "mate tu trabajo soñado.",
      sub: "En Australia, el 75% de los CV son rechazados por una IA antes de que un humano los vea. AussieCV reescribe el tuyo en el formato exacto que Seek y los ATS esperan — en menos de 60 segundos.",
      cta_primary: "Optimizar mi CV gratis",
      cta_secondary: "Cómo funciona",
      trust: "Sin cuenta · Sin email · Tus datos siguen siendo tuyos",
    },
    stats: {
      rejected: { n: "75%", t: "de los CV filtrados por el ATS antes de ser leídos por un humano" },
      seek: { n: "92%", t: "de los reclutadores australianos usan Seek" },
      time: { n: "60s", t: "para tener tu CV y carta de presentación listos" },
    },
    how: {
      title: "Cómo funciona",
      sub: "Cuatro pasos. Cero fricción.",
      s1: { t: "Sube tu CV", d: "Arrastra tu CV actual (PDF o Word). Extraemos el texto en segundos." },
      s2: { t: "Pega el anuncio de Seek", d: "Pega la URL del anuncio. Lo recuperamos automáticamente." },
      s3: { t: "Dinos lo esencial", d: "Visa, ciudad, sector. 30 segundos, no más." },
      s4: { t: "Recibe tu CV optimizado", d: "Puntuación ATS, CV reescrito, carta de presentación, descarga PDF/Word. Listo." },
    },
    why: {
      title: "Por qué los reclutadores australianos filtran tan duro",
      p1: "Todos los grandes empleadores en Australia — minas, hostelería, salud, logística — usan software ATS. El robot lee tu CV antes que cualquier humano.",
      p2: "Si tus palabras clave no coinciden con el anuncio, si las fechas se ven raras, si tu visa no está clara, o si tu CV tiene columnas/foto/diseño raro — quedas fuera. En silencio.",
      p3: "AussieCV conoce todas las reglas del formato australiano y todas las palabras clave que tu sector exige. Reescribimos tu CV para que el robot diga SÍ y un humano realmente te lea.",
    },
    wizard: {
      step: "Paso",
      of: "de",
      back: "Atrás",
      next: "Siguiente",
      optimize: "Optimizar mi CV",
      s1: {
        title: "Sube tu CV actual",
        sub: "PDF, Word o texto. Extraemos todo automáticamente.",
        drop: "Suelta tu CV aquí o haz clic para buscar",
        accepted: "PDF · DOCX · TXT — máx 10 MB",
        or: "o pega el texto de tu CV",
        placeholder: "Pega aquí el texto de tu CV...",
        uploading: "Leyendo tu CV...",
        parsed: "CV cargado",
      },
      s2: {
        title: "El anuncio al que postulas",
        sub: "3 formas de importar: URL, pegar texto o subir una captura.",
        url_label: "URL de Seek",
        url_placeholder: "https://www.seek.com.au/job/...",
        fetch: "Recuperar anuncio",
        fetching: "Recuperando...",
        loaded: "Anuncio cargado",
        or: "o pega la descripción",
        text_placeholder: "Pega aquí el anuncio completo...",
        seek_blocked_title: "Seek bloquea la recuperación automática",
        seek_blocked_body: "¡Sin problema! Copia y pega el texto del anuncio en el campo de abajo, funciona igual de bien.",
      },
      s3: {
        title: "Algunos datos esenciales",
        sub: "Menos de 30 segundos. Solo pedimos lo que el ATS realmente verifica.",
        visa: "Estado de visa",
        location: "Ciudad actual en Australia",
        location_ph: "ej. Perth, Karratha, Sydney, Darwin...",
        sector: "Sector objetivo",
        notes: "¿Algo específico que mencionar? (opcional)",
        notes_ph: "Certificados que tienes, disponibilidad, experiencia específica...",
        language: "Idioma del informe",
      },
      s4: {
        title: "Optimizando tu CV",
        line1: "Leyendo tu CV original...",
        line2: "Extrayendo palabras clave del anuncio...",
        line3: "Reescribiendo en formato ATS australiano...",
        line4: "Generando tu carta de presentación...",
        line5: "Calculando tu puntuación de coincidencia...",
        wait: "Suele tardar entre 20 y 40 segundos.",
      },
    },
    visas: {
      "417": "417 Working Holiday Visa",
      "462": "462 Work & Holiday Visa",
      "482": "482 Visa TSS",
      "494": "494 Skilled Employer Sponsored",
      "500": "500 Visa de Estudiante",
      "PR": "Residente Permanente",
      "Citizen": "Ciudadano Australiano",
      "Other": "Otro / Bridging",
    },
    sectors: {
      mining: "Minería / FIFO",
      hospitality: "Hostelería / Café / Bar",
      construction: "Construcción / Oficios",
      farm: "Granja / Agricultura (88 días)",
      healthcare: "Salud / Aged care",
      tech: "Tech / Oficina",
      retail: "Retail / Atención al cliente",
      logistics: "Logística / Conducción",
      other: "Otro",
    },
    results: {
      title: "Tu CV optimizado está listo",
      sub: "Descárgalo, ajústalo si quieres, y postula con confianza.",
      before: "Antes",
      after: "Después",
      score_label: "Puntuación ATS",
      issues: "Problemas que corregimos",
      improvements: "Lo que mejoramos",
      missing: "Puntos críticos que podrías estar perdiendo",
      checklist: "Tu checklist de postulación",
      cv_tab: "CV optimizado",
      cover_tab: "Carta de presentación",
      download_pdf: "Descargar CV (PDF)",
      download_docx: "Descargar CV (Word)",
      download_cover: "Descargar carta (PDF)",
      download_action_plan: "Plan de acción (PDF)",
      redo: "Empezar de nuevo",
      copy: "Copiar",
      copied: "¡Copiado!",
    },
    faq: {
      title: "Preguntas frecuentes",
      q1: "¿Mis datos están seguros?",
      a1: "No requerimos cuenta ni email. Tu CV es procesado por nuestro motor de IA seguro para ser reescrito en formato ATS australiano y luego se almacena de forma anónima para mejorar el servicio. Nunca vendemos ni compartimos.",
      q2: "¿Es realmente gratis?",
      a2: "Sí, el MVP es 100% gratuito. En el futuro podríamos introducir un plan premium (CV ilimitados, variantes de carta, optimizador de LinkedIn), pero tu uso actual sigue siendo gratis.",
      q3: "¿Funciona para trabajos fuera de la minería?",
      a3: "Absolutamente. Cubrimos Hostelería, Construcción, Granja, Salud, Oficios, Tech, Retail, Logística y más. La IA adapta las palabras clave a tu sector.",
      q4: "¿Necesito una dirección australiana?",
      a4: "No. Si estás en el extranjero, presentamos tu perfil como 'willing to relocate'. Si ya estás en Australia, ser local es una gran ventaja que destacamos.",
      q5: "¿Y si no tengo todos los certificados requeridos?",
      a5: "Te decimos exactamente cuáles te faltan y dónde obtenerlos (White Card, RSA, Standard 11, PTD, etc.). Nunca inventamos certificaciones que no tienes.",
    },
    errors: {
      upload: "No se pudo leer el archivo. Prueba con un PDF o DOCX.",
      seek: "No se pudo recuperar la URL de Seek. Pega el texto del anuncio.",
      optimize: "Algo salió mal. Inténtalo de nuevo.",
      missing_cv: "Sube o pega primero tu CV.",
      missing_job: "Añade primero el anuncio.",
      missing_profile: "Completa visa, ciudad y sector.",
    },
    footer: {
      tagline: "Construido por backpackers, para backpackers.",
      legal: "No afiliado a Seek ni a ningún empleador.",
    },
  },

  pt: {
    nav: { cta: "Otimizar meu CV" },
    hero: {
      overline: "Feito para backpackers em 417 / 462",
      h1_a: "Não deixes um algoritmo",
      h1_b: "matar o trabalho dos teus sonhos.",
      sub: "Na Austrália, 75% dos CV são rejeitados por uma IA antes de um humano os ver. AussieCV reescreve o teu no formato exato que o Seek e os ATS esperam — em menos de 60 segundos.",
      cta_primary: "Otimizar meu CV grátis",
      cta_secondary: "Como funciona",
      trust: "Sem conta · Sem email · Os teus dados continuam teus",
    },
    stats: {
      rejected: { n: "75%", t: "dos CV são filtrados pelo ATS antes de serem lidos por um humano" },
      seek: { n: "92%", t: "dos recrutadores australianos usam o Seek" },
      time: { n: "60s", t: "para teres o teu CV e carta prontos" },
    },
    how: {
      title: "Como funciona",
      sub: "Quatro passos. Zero atrito.",
      s1: { t: "Carrega o teu CV", d: "Arrasta o teu CV atual (PDF ou Word). Extraímos o texto em segundos." },
      s2: { t: "Cola o anúncio do Seek", d: "Cola o URL do anúncio. Recuperamo-lo automaticamente." },
      s3: { t: "Diz-nos o essencial", d: "Visto, cidade, setor. 30 segundos, nada mais." },
      s4: { t: "Recebe o teu CV otimizado", d: "Pontuação ATS, CV reescrito, carta de apresentação, download PDF/Word. Pronto." },
    },
    why: {
      title: "Porque é que os recrutadores australianos filtram tão duro",
      p1: "Todos os grandes empregadores na Austrália — minas, hotelaria, saúde, logística — usam software ATS. O robô lê o teu CV antes de qualquer humano.",
      p2: "Se as tuas palavras-chave não correspondem ao anúncio, se as datas parecem estranhas, se o teu visto não está claro, ou se o teu CV tem colunas/foto/design estranho — estás fora. Em silêncio.",
      p3: "AussieCV conhece todas as regras do formato australiano e todas as palavras-chave que o teu setor exige. Reescrevemos o teu CV para que o robô diga SIM e um humano realmente te leia.",
    },
    wizard: {
      step: "Passo",
      of: "de",
      back: "Voltar",
      next: "Seguinte",
      optimize: "Otimizar meu CV",
      s1: {
        title: "Carrega o teu CV atual",
        sub: "PDF, Word ou texto. Extraímos tudo automaticamente.",
        drop: "Larga o teu CV aqui ou clica para procurar",
        accepted: "PDF · DOCX · TXT — máx 10 MB",
        or: "ou cola o texto do teu CV",
        placeholder: "Cola aqui o texto do teu CV...",
        uploading: "A ler o teu CV...",
        parsed: "CV carregado",
      },
      s2: {
        title: "O anúncio ao qual te candidatas",
        sub: "3 formas de importar: URL, colar texto ou carregar uma captura.",
        url_label: "URL do Seek",
        url_placeholder: "https://www.seek.com.au/job/...",
        fetch: "Recuperar anúncio",
        fetching: "A recuperar...",
        loaded: "Anúncio carregado",
        or: "ou cola a descrição",
        text_placeholder: "Cola aqui o anúncio completo...",
        seek_blocked_title: "O Seek bloqueia a recuperação automática",
        seek_blocked_body: "Sem problema! Copia e cola o texto do anúncio no campo abaixo, funciona igualmente bem.",
      },
      s3: {
        title: "Alguns dados essenciais",
        sub: "Menos de 30 segundos. Só pedimos o que o ATS realmente verifica.",
        visa: "Estado do visto",
        location: "Cidade atual na Austrália",
        location_ph: "ex. Perth, Karratha, Sydney, Darwin...",
        sector: "Setor alvo",
        notes: "Algo específico a mencionar? (opcional)",
        notes_ph: "Certificados que tens, disponibilidade, experiência específica...",
        language: "Idioma do relatório",
      },
      s4: {
        title: "A otimizar o teu CV",
        line1: "A ler o teu CV original...",
        line2: "A extrair palavras-chave do anúncio...",
        line3: "A reescrever no formato ATS australiano...",
        line4: "A gerar a tua carta de apresentação...",
        line5: "A calcular a tua pontuação de correspondência...",
        wait: "Normalmente demora 20 a 40 segundos.",
      },
    },
    visas: {
      "417": "417 Working Holiday Visa",
      "462": "462 Work & Holiday Visa",
      "482": "482 Visto TSS",
      "494": "494 Skilled Employer Sponsored",
      "500": "500 Visto de Estudante",
      "PR": "Residente Permanente",
      "Citizen": "Cidadão Australiano",
      "Other": "Outro / Bridging",
    },
    sectors: {
      mining: "Minas / FIFO",
      hospitality: "Hotelaria / Café / Bar",
      construction: "Construção / Ofícios",
      farm: "Quinta / Agricultura (88 dias)",
      healthcare: "Saúde / Aged care",
      tech: "Tech / Escritório",
      retail: "Retalho / Apoio ao cliente",
      logistics: "Logística / Condução",
      other: "Outro",
    },
    results: {
      title: "O teu CV otimizado está pronto",
      sub: "Descarrega-o, ajusta se quiseres, e candidata-te com confiança.",
      before: "Antes",
      after: "Depois",
      score_label: "Pontuação ATS",
      issues: "Problemas que corrigimos",
      improvements: "O que melhorámos",
      missing: "Pontos críticos que te podem estar a faltar",
      checklist: "A tua checklist de candidatura",
      cv_tab: "CV otimizado",
      cover_tab: "Carta de apresentação",
      download_pdf: "Descarregar CV (PDF)",
      download_docx: "Descarregar CV (Word)",
      download_cover: "Descarregar carta (PDF)",
      download_action_plan: "Plano de ação (PDF)",
      redo: "Recomeçar",
      copy: "Copiar",
      copied: "Copiado!",
    },
    faq: {
      title: "Perguntas frequentes",
      q1: "Os meus dados estão seguros?",
      a1: "Não exigimos conta nem email. O teu CV é processado pelo nosso motor de IA seguro para ser reescrito no formato ATS australiano e depois armazenado de forma anónima para melhorar o serviço. Nunca vendemos nem partilhamos.",
      q2: "É mesmo grátis?",
      a2: "Sim, o MVP é 100% gratuito. No futuro poderemos introduzir um plano premium (CV ilimitados, variantes de carta, otimizador LinkedIn), mas o teu uso atual continua grátis.",
      q3: "Funciona para empregos fora das minas?",
      a3: "Absolutamente. Cobrimos Hotelaria, Construção, Quintas, Saúde, Ofícios, Tech, Retalho, Logística e mais. A IA adapta as palavras-chave ao teu setor.",
      q4: "Preciso de um endereço australiano?",
      a4: "Não. Se estás no estrangeiro, apresentamos o teu perfil como 'willing to relocate'. Se já estás na Austrália, ser local é uma enorme vantagem que destacamos.",
      q5: "E se não tenho todos os certificados exigidos?",
      a5: "Dizemos-te exatamente quais te faltam e onde os obter (White Card, RSA, Standard 11, PTD, etc.). Nunca inventamos certificados que não tens.",
    },
    errors: {
      upload: "Não foi possível ler o ficheiro. Tenta um PDF ou DOCX.",
      seek: "Não foi possível recuperar o URL do Seek. Cola o texto do anúncio.",
      optimize: "Algo correu mal. Tenta novamente.",
      missing_cv: "Carrega ou cola primeiro o teu CV.",
      missing_job: "Adiciona primeiro o anúncio.",
      missing_profile: "Preenche visto, cidade e setor.",
    },
    footer: {
      tagline: "Construído por backpackers, para backpackers.",
      legal: "Não afiliado ao Seek nem a qualquer empregador.",
    },
  },
};
